import { OData } from "@odata/OData";
import { IPermissionSettings } from "@utils/permissionUtils";
import { TFunctionResult } from "i18next";
import React from "react";
import { Layout } from "react-grid-layout";
import { LinkProps } from "react-router-dom";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { IPermissionContext } from "../../contexts/permissionContext/PermissionContext";
import { Status } from "../../enums";
import { Theme } from "../../theme";
import { TChartValueFormatterFn } from "../charts/Charts.types";
import { TInfoValue } from "../smart/FieldInfo";
import { IColumn, IRow } from "../table";
import { IChartTileData } from "../tiles/chartTile/ChartTile";

const GridTileBaseSize = 125;
export const GridGap = 32;
export const GridTileSize = GridTileBaseSize + GridGap;

export type TLayout = Layout;

export type TDashboardTileConfig = Pick<TLayout, "i" | "w" | "h">;
export type TDashboardLayout = TLayout[];

/**
 * TileTypes:
 *  - Custom - complete custom render with component
 *  - Info - text content with simple information, one or more cells with severity indicator
 *  - Table - renders table according to definition
 */
export enum DashboardTileType {
    Info = "Info",
    Custom = "Custom",
    Table = "Table",
    Link = "Link",
    Chart = "Chart",
    Group = "Group"
}

export interface IDashboardLayoutConfig {
    cols?: number;
    layout?: TDashboardLayout;
    hash?: string; // hash of the basic definition, used to detect changes and forgot saved layout if definition has changed
}

interface ITileSize {
    w: number;
    h: number;
}

export interface IDashboardTileComponentProps<InfoType extends IDashboardTileCommonInfo = IDashboardTileCommonInfo> {
    inEditMode?: boolean;
    info: InfoType;
    isLight?: boolean;
}

/**
 * ---- Methods and interfaces to obtain Dashboard Tile data ----
 */
export interface IGetTileDataArgs {
    oData: OData;
    context?: IAppContext;
    permissionContext?: IPermissionContext;
    signal?: AbortSignal;
}

export type TGetDataFn<T> = (args: IGetTileDataArgs) => Promise<T>;
export type TDataValue<T> = T | TGetDataFn<T>;

function isGetDataFn<T = unknown>(prop: unknown | TGetDataFn<T>): prop is TGetDataFn<T> {
    return typeof prop === "function";
}

export function getTileData<T>(prop: TDataValue<T>, args: IGetTileDataArgs): Promise<T> {
    if (isGetDataFn(prop)) {
        return prop(args);
    }
    return Promise.resolve(prop);
}

type TInfoTileDataValue = number | string;

export interface IInfoTileDataCell {
    value: TInfoTileDataValue;
    severity?: Status;
    label?: TFunctionResult;
    unit?: TFunctionResult;
    iconName?: string;
    link?: TInfoValue<LinkProps["to"]>;
    formatter?: TDashboardInfoTileFormatter;
    size?: number; // number of dashboard base tiles, which it should take, default 1
}

export interface ITableTileData {
    rows: IRow[];
    columns: IColumn[];
}

export type TDashboardInfoTileFormatter = (value: TInfoTileDataValue) => React.ReactNode;
export type TBasicLinkTileConfig = Partial<ILinkTileInfo> & Pick<ILinkTileInfo, "link">;

/**
 * ---- Dashboard Tile Definition interfaces
 */
export interface IDashboardTileCommonInfo extends IPermissionSettings {
    // for config rendering
    title: string;
    subtitle?: string;
    tooltip?: string;
    link?: TInfoValue<LinkProps["to"]>;

    type: DashboardTileType;
    size: ITileSize;
    backgroundColor?: keyof Theme;
    isVisible?: TInfoValue<Promise<boolean> | boolean>;
    // user cannot remove the tile from dashboards by unchecking it in settings
    // used for PurchaseTile
    cannotBeRemovedInSettings?: boolean;
}

export interface ICustomTileInfo extends IDashboardTileCommonInfo {
    type: DashboardTileType.Custom;
    component: React.ComponentType<IDashboardTileComponentProps>;
}

export interface IInfoTileInfo extends IDashboardTileCommonInfo {
    type: DashboardTileType.Info;
    firstLetterUppercase?: boolean;
    infoData: TDataValue<IInfoTileDataCell[]>;
    formatter?: TDashboardInfoTileFormatter;
}

export interface IChartTileInfo extends IDashboardTileCommonInfo {
    type: DashboardTileType.Chart;
    chartData: TDataValue<IChartTileData>;
    formatter?: TChartValueFormatterFn;
}

export interface ITableTileInfo extends IDashboardTileCommonInfo {
    type: DashboardTileType.Table;
    noDataText?: string;
    tableData?: TDataValue<ITableTileData>;
}

export interface ILinkTileInfo extends IDashboardTileCommonInfo {
    type: DashboardTileType.Link;
    iconName: string;
    count?: TDataValue<number>;
}

export interface IGroupTileInfo extends IDashboardTileCommonInfo {
    type: DashboardTileType.Group;
    items: TBasicLinkTileConfig[];
}

export type TDashboardTileInfo = ICustomTileInfo | IInfoTileInfo | ITableTileInfo | ILinkTileInfo | IChartTileInfo | IGroupTileInfo;
export type TTileDefinition<T extends string = string> = Record<T, TDashboardTileInfo>;

export interface IDashboardGroupDef<Keys extends string = string> {
    id: string;
    title: string;
    tiles: Keys[];
    allTiles: Keys[];
}

export interface IDashboardDefinition<R extends string = string> {
    title?: string;
    tileDefinition: TTileDefinition<R>;
    groups: IDashboardGroupDef<R>[];
    isVisible?: TInfoValue<boolean>;
}
